"use client";

import React, { useCallback, useMemo, useState } from "react";
import {
  Card,
  Modal,
  Button as ButtonComponent,
  AlertModal,
  Input,
  Select,
  Textarea,
  Switch,
} from "@/components";
import { DictionaryProps } from "@/types";
import { useNotificationsSchedule } from "@/hooks";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { notificationsScheduleSchema } from "@/validations";
import { renderTemplate } from "@/utils/templateRenderer";
import { Plus, Pencil, Trash, Eye } from "lucide-react";
import type { ScheduleNotificationTemplate } from "@/types/schedules";

type Props = { dict: DictionaryProps["dict"] };

const NotificationsScheduleManager: React.FC<Props> = ({ dict }) => {
  const {
    templates,
    loading,
    refresh,
    createTemplate,
    updateTemplate,
    removeTemplate,
  } = useNotificationsSchedule(() => refresh());

  const [editingTemplate, setEditingTemplate] =
    useState<ScheduleNotificationTemplate | null>(null);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [toDeleteId, setToDeleteId] = useState<number | null>(null);
  const [previewMessage, setPreviewMessage] = useState<string | null>(null);
  const [openForm, setOpenForm] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<any>({
    resolver: yupResolver(notificationsScheduleSchema as any),
    defaultValues: {
      event: "scheduled",
      target: "both",
      days_before: 60,
      title: "",
      message: "",
      active: true,
    },
  });

  const openCreate = useCallback(() => {
    setEditingTemplate(null);
    reset({
      event: "scheduled",
      target: "both",
      days_before: 60,
      title: "",
      message: "",
      active: true,
    });
    setOpenForm(true);
  }, [reset]);

  const openEdit = useCallback(
    (tpl: any) => {
      setEditingTemplate(tpl);
      const normalizedDays =
        tpl?.event === "reminder"
          ? (tpl?.days_before ?? 60)
          : (tpl?.days_before ?? 0);
      reset({ ...tpl, days_before: normalizedDays });
      setOpenForm(true);
    },
    [reset],
  );

  const onSubmit = useCallback(
    async (data: any) => {
      const daysBefore =
        data?.event === "reminder" ? (data?.days_before ?? 60) : 0;
      const payload = {
        ...data,
        days_before: daysBefore,
        active: !!data?.active,
      };
      if (editingTemplate && editingTemplate.id) {
        await updateTemplate({ id: editingTemplate.id, ...payload });
      } else {
        await createTemplate(payload);
      }
      reset();
      setOpenForm(false);
    },
    [editingTemplate, createTemplate, updateTemplate, reset],
  );

  const handleDelete = useCallback((tpl: any) => {
    setToDeleteId(tpl.id);
    setOpenDeleteConfirm(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (toDeleteId != null) await removeTemplate(toDeleteId);
    setToDeleteId(null);
    setOpenDeleteConfirm(false);
  }, [toDeleteId, removeTemplate]);

  const handleToggleActive = useCallback(
    async (tpl: ScheduleNotificationTemplate) => {
      const daysBefore =
        tpl.event === "reminder"
          ? (tpl.days_before ?? 60)
          : (tpl.days_before ?? 0);
      await updateTemplate({
        id: tpl.id as number,
        event: tpl.event,
        target: tpl.target,
        days_before: daysBefore,
        title: tpl.title,
        message: tpl.message,
        active: !tpl.active,
      });
    },
    [updateTemplate],
  );

  const handlePreview = useCallback((tpl: any) => {
    const message = typeof tpl === "string" ? tpl : tpl?.message;
    const example = {
      primeiro_nome: "Ana",
      nome_completo: "Ana Silva",
      profissional_nome: "João Souza",
      data_hora: "16/02/2026 14:00",
      data: "16/02/2026",
      hora: "14:00",
      servicos: ["Corte", "Hidratação"],
      valor_total: "R$ 120,00",
      codigo_agendamento: "A123",
    };
    setPreviewMessage(renderTemplate(message || "", example));
  }, []);

  const templateRows = useMemo(() => templates || [], [templates]);

  const eventOptions = useMemo(
    () => [
      { value: "scheduled", label: dict["Scheduled"] || "scheduled" },
      { value: "finalized", label: dict["Finalized"] || "finalized" },
      { value: "reminder", label: dict["Reminder"] || "reminder" },
    ],
    [dict],
  );

  const targetOptions = useMemo(
    () => [
      { value: "client", label: dict["Client"] || "client" },
      { value: "professional", label: dict["Professional"] || "professional" },
      { value: "both", label: dict["Both"] || "both" },
    ],
    [dict],
  );

  const eventLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    eventOptions.forEach((opt) => {
      map[opt.value] = opt.label;
    });
    return map;
  }, [eventOptions]);

  const targetLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    targetOptions.forEach((opt) => {
      map[opt.value] = opt.label;
    });
    return map;
  }, [targetOptions]);

  // normalize message error for safe render
  const messageFieldError = (() => {
    const m = (errors as any)?.message;
    if (!m) return null;
    if (typeof m === "string") return m;
    if (typeof m?.message === "string") return m.message;
    return String(m);
  })();

  return (
    <div>
      <div className="mb-4 flex justify-end">
        <ButtonComponent
          onClick={openCreate}
          icon={<Plus />}
          text={dict["New Schedule Template"]}
          variant="secondary"
        />
      </div>

      <div className="w-full overflow-auto">
        <table className="w-full table-auto text-sm">
          <thead>
            <tr className="text-left">
              <th className="p-2">{dict["Template Title"] || "Title"}</th>
              <th className="p-2">{dict.Event}</th>
              <th className="p-2">{dict.Target}</th>
              <th className="p-2">{dict.Active}</th>
              <th className="p-2">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {templateRows.length === 0 ? (
              <tr>
                <td
                  colSpan={6}
                  className="p-6 text-center text-muted-foreground"
                >
                  Sem templates cadastrados.
                </td>
              </tr>
            ) : (
              templateRows.map((tpl: any) => (
                <tr key={tpl.id} className="border-t">
                  <td className="p-2 align-top">
                    {tpl.title || (tpl.message || "").slice(0, 20)}
                  </td>
                  <td className="p-2 align-top">
                    {eventLabelMap[tpl.event] || tpl.event}
                  </td>
                  <td className="p-2 align-top">
                    {targetLabelMap[tpl.target] || tpl.target}
                  </td>
                  <td className="p-2 align-top">
                    <Switch
                      checked={!!tpl.active}
                      onChange={() => handleToggleActive(tpl)}
                    />
                  </td>
                  <td className="p-2 align-top">
                    <div className="flex gap-2">
                      <button
                        className="btn btn-ghost"
                        onClick={() => handlePreview(tpl)}
                        title={dict.message}
                      >
                        <Eye size={18} className="cursor-pointer" />
                      </button>
                      <button
                        className="btn btn-ghost"
                        onClick={() => openEdit(tpl)}
                        title={dict.Edit}
                      >
                        <Pencil size={18} className="cursor-pointer" />
                      </button>
                      <button
                        className="btn btn-ghost text-destructive"
                        onClick={() => handleDelete(tpl)}
                        title={dict["Delete template?"]}
                      >
                        <Trash size={18} className="cursor-pointer" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        titleModal={
          editingTemplate
            ? dict["Edit Schedule Template"] || "Edit"
            : dict["New Schedule Template"] || "New Template"
        }
        open={openForm}
        onOpenChange={setOpenForm}
      >
        <Card>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid gap-4">
              <div>
                <label className="block text-sm mb-1">{dict.Event}</label>
                <Select {...register("event")} options={eventOptions} />
              </div>

              <input type="hidden" {...register("days_before")} />

              <div>
                <label className="block text-sm mb-1">{dict.Target}</label>
                <Select {...register("target")} options={targetOptions} />
              </div>

              <div>
                <label className="block text-sm mb-1">
                  {dict["Template Title"]}
                </label>
                <Input {...register("title")} />
              </div>

              <div>
                <label className="block text-sm mb-1">
                  {dict["Message (Up to 500 Characters)"]}
                </label>
                <Textarea {...register("message")} maxLength={500} rows={5} />
                <div className="text-xs text-muted-foreground mt-2 space-y-1 border rounded bg-muted/30 p-3">
                  <div>{dict["Schedule placeholders intro"]}</div>
                  <div>{dict["Schedule placeholders scheduled"]}</div>
                  <div>{dict["Schedule placeholders finalized"]}</div>
                  <div>{dict["Schedule placeholders cleanup"]}</div>
                  <div className="text-destructive">
                    {messageFieldError || null}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="block text-sm">{dict.Active}</label>
                <Switch
                  checked={!!watch("active")}
                  onChange={(v) => setValue("active", v)}
                />
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <ButtonComponent
                  text={dict.Cancel || "Cancel"}
                  variant="default"
                  type="button"
                  onClick={() => {
                    reset();
                    setOpenForm(false);
                  }}
                />
                <ButtonComponent
                  text={dict.Save || "Save"}
                  variant="success"
                  type="submit"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </form>
        </Card>
      </Modal>

      {/* Preview modal */}
      <Modal
        titleModal={dict.Preview || "Preview"}
        open={!!previewMessage}
        onOpenChange={() => setPreviewMessage(null)}
      >
        <Card>
          <div className="whitespace-pre-wrap">{previewMessage}</div>
        </Card>
      </Modal>

      {/* Delete confirmation */}
      <AlertModal
        messageAlert={dict["Are you sure you want to delete this template?"]}
        open={openDeleteConfirm}
        onOpenChange={setOpenDeleteConfirm}
        confirm={confirmDelete}
        cancel={() => setOpenDeleteConfirm(false)}
      />
    </div>
  );
};

export default NotificationsScheduleManager;
